import {
  getAllCategoriesWithSubcategories,
  getCampaignMenuItems
} from '#src/products/get-all-categories'
import {
  filterSubCategories,
  getCategoryWithSubCategoryBySlug,
  getSubCategoryBySlug
} from '#src/products/util'
import { parseCookies } from 'nookies'
import { getMarketFromCtx } from '#lib/get-market'
import {
  anonymousIdHeader,
  addLastOrderFlag,
  outpostCutoffFlag,
  filterAllergensFlag
} from '#constants'
import { fetchBanner } from '#src/products/get-feature-banner'
import CoffeeWizardModal from '#src/coffee-wizard'
import { useMemo, useState } from 'react'
import { Heading } from '@pretamanger/component-library'
import List from '#src/products/components/list/v2'
import PropTypes from 'prop-types'
import { category, commonPageProps } from '#proptypes/index'
import { getLogger } from '#api/logger-with-context'
import ProductsLayoutWithLeftNav from '../components/layout/with-side-nav'
import PageSlots from '#src/common/components/page-slots'
import getProductsByCategory from './get-products-by-category'
import { ContentWrapper } from './styles'
import { getLaunchDarklyFlag } from '#src/api/flags'
import { LastOrder } from '#src/products/components/last-order'
import { useLastOrder } from '#hooks/useLastOrder'

import usePromotions from '#src/common/hooks/usePromotions'
import { InPagePromotion } from '#src/common/app/components/announcements/promotion'
import { useRequireBag } from '../../common/hooks/useRequireBag'
import { bagSelector } from '#src/state/bag-state-slice'
import { useAppSelector } from '#src/state/redux-hooks'
import { useCategoryData } from './use-category-data'
import { removeUndefinedProperties } from '#src/common/lib/object'
import FilterAllergensModal from '../components/filter-allergens-modal'
import FilterAllergensPills from '../components/filter-allergens-pills'
import EmptyProducts from '../components/empty-products'

export const getServerSideProps = async ctx => {
  const log = getLogger()
  const {
    alternateLanguagePages,
    commonProps,
    params = {},
    isTransactional,
    gtmPageProps
  } = ctx
  const market = getMarketFromCtx(ctx)
  const cookies = parseCookies(ctx)
  const user = cookies[anonymousIdHeader]

  const categories = await getAllCategoriesWithSubcategories(
    ctx,
    market.id,
    isTransactional
  )
  if (!categories) {
    log.warn(
      { category: params.category },
      'Product Category Page:: category not found'
    )
    return { notFound: true }
  }
  const selectedCategorySlug =
    params.subcategory || params.category || categories[0].slug

  let selectedCategory = params.subcategory
    ? getSubCategoryBySlug(selectedCategorySlug, params.category, categories)
    : getCategoryWithSubCategoryBySlug(selectedCategorySlug, categories)

  if (!selectedCategory) {
    log.warn(
      { category: params.category },
      'Product Category Page:: category not found'
    )
    return { notFound: true }
  }
  const selectedSubCategory =
    selectedCategory?.subcategories?.length > 0
      ? selectedCategory.subcategories[0]
      : []

  selectedCategory = filterSubCategories(selectedCategory)

  if (!selectedCategory) {
    log.warn(
      { category: params.category },
      'Product Category Page:: category not found'
    )
    return { notFound: true }
  }
  const cutoffTimeEnabledFlag = await getLaunchDarklyFlag(
    outpostCutoffFlag,
    user,
    market?.id
  )
  const getProducts = async () => {
    if (selectedCategory.subcategories?.length) {
      await Promise.all(
        selectedCategory.subcategories.map(async (subcategory, index) => {
          const products = await getProductsByCategory(subcategory, ctx)
          selectedCategory.subcategories[index].products = products
        })
      )
    } else {
      selectedCategory.products = await getProductsByCategory(
        selectedCategory,
        ctx
      )
    }
  }

  const [banner, useAddLastOrderFlag, useFilterAllergensFlag] =
    await Promise.all([
      fetchBanner(selectedCategory.id),
      getLaunchDarklyFlag(addLastOrderFlag, user, market?.id),
      getLaunchDarklyFlag(filterAllergensFlag, user, market?.id),
      isTransactional ? Promise.resolve() : getProducts()
    ])

  const campaignMenuItems = !isTransactional
    ? getCampaignMenuItems(commonProps)
    : []

  return removeUndefinedProperties({
    props: {
      ...(banner ? { banner } : {}),
      ...(alternateLanguagePages && { alternateLanguagePages }),
      selectedCategory,
      categories: campaignMenuItems.concat(categories),
      selectedSubCategory,
      useAddLastOrderFlag,
      gtmPageProps,
      cutoffTimeEnabledFlag,
      useFilterAllergensFlag,
      ...commonProps
    }
  })
}

const ProductCategoryPage = ({
  banner,
  categories,
  selectedCategory: _selectedCategory,
  selectedSubCategory,
  isTransactional,
  settings,
  useAddLastOrderFlag,
  useFilterAllergensFlag
}) => {
  useRequireBag(isTransactional)
  const { dataForInPagePromo, currentPromotion, showInPagePromo } =
    usePromotions({ promotions: settings.promotions, isTransactional })
  const inPagePromo =
    showInPagePromo && currentPromotion ? (
      <InPagePromotion
        {...currentPromotion}
        trackingCode={dataForInPagePromo.trackingCode}
        type='in-page'
      />
    ) : null

  const [isCoffeeWizardOpen, setCoffeeWizardOpen] = useState(false)
  const [coffeeWizardProduct, setCoffeeWizardProduct] = useState(null)
  const [isOpenAllergensFilter, setOpenAllegensFilter] = useState(false)
  const bag = useAppSelector(bagSelector)

  const {
    category: selectedCategory,
    error,
    isLoading: isContentLoading
  } = useCategoryData({
    selectedCategory: _selectedCategory,
    selectedSubCategory,
    isTransactional,
    bag
  })

  const { lastOrder, hasBeenAddedToBag, setHasBeenAddedToBag } = useLastOrder(
    useAddLastOrderFlag,
    isTransactional,
    bag.locale,
    bag?.orderDetails?.fulfillingShopId,
    bag?.orderDetails?.pickupTimeEnd,
    bag?.orderDetails?.pickupTimeStart,
    bag?.orderDetails?.orderType
  )

  if (isTransactional && error) {
    throw new Error(error)
  }

  const handleOpenCoffeeWizard = async product => {
    setCoffeeWizardOpen(true)
    setCoffeeWizardProduct(product)
  }
  const handleCloseCoffeeWizard = () => {
    setCoffeeWizardOpen(false)
    setCoffeeWizardProduct(null)
  }

  const coffeeWizard = {
    enabled: true,
    load: handleOpenCoffeeWizard
  }

  const openAllergensFilter = () => setOpenAllegensFilter(true)
  const closeAllergensFilter = () => setOpenAllegensFilter(false)

  const isProductsEmpty = useMemo(() => {
    const subcategories = selectedCategory.subcategories
    if (subcategories?.length) {
      for (let index = 0; index < subcategories.length; index++) {
        const subcategory = subcategories[index]
        const products = subcategory.products
        if (products?.length) {
          return false
        }
      }
      return true
    }
    if (selectedCategory.products?.length) {
      return false
    }
    return true
  }, [selectedCategory])

  return (
    <>
      {banner && !(!banner.displayOnTransactional && isTransactional) && (
        <div className='mt-16' data-testid='category-page-banner'>
          <PageSlots slots={[banner]} settings={settings} />
        </div>
      )}
      {useFilterAllergensFlag && (
        <FilterAllergensModal
          isOpen={isOpenAllergensFilter}
          onClose={closeAllergensFilter}
        />
      )}
      <ProductsLayoutWithLeftNav
        heading={
          <>
            {useFilterAllergensFlag && <FilterAllergensPills />}
            {inPagePromo}
            {!isProductsEmpty && (
              <Heading level='h1' styleOverride='headingMd' id='page-title'>
                {selectedCategory.name}
              </Heading>
            )}
          </>
        }
        categories={categories}
        selectedCategoryIndex={categories.findIndex(
          category => category.id === _selectedCategory.id
        )}
        isTransactional={isTransactional}
        isContentLoading={isContentLoading}
        openModal={openAllergensFilter}
        useFilterAllergensFlag={useFilterAllergensFlag}
      >
        {!isContentLoading && (
          <ContentWrapper>
            {isCoffeeWizardOpen && (
              <CoffeeWizardModal
                onClose={handleCloseCoffeeWizard}
                product={coffeeWizardProduct}
              />
            )}
            {!hasBeenAddedToBag && (
              <LastOrder
                lastOrder={lastOrder}
                onLastOrderAddedToBag={setHasBeenAddedToBag}
              />
            )}
            {isProductsEmpty && <EmptyProducts />}
            {!isProductsEmpty && selectedCategory.subcategories?.length ? (
              selectedCategory.subcategories.map(subcategory => (
                <List
                  selectedCategoryName={selectedCategory.name}
                  isTransactional={isTransactional}
                  key={subcategory.id}
                  selectedCategory={selectedCategory}
                  selectedSubCategory={selectedSubCategory}
                  name={subcategory.name}
                  products={subcategory.products}
                  placeholder={settings.placeholderImage}
                  unavailableForOrderAhead={!subcategory.availableForOrderAhead}
                  coffeeWizard={coffeeWizard}
                />
              ))
            ) : (
              <List
                selectedCategoryName={selectedCategory.name}
                isTransactional={isTransactional}
                key={selectedCategory.id}
                selectedCategory={selectedCategory}
                selectedSubCategory={selectedSubCategory}
                products={selectedCategory.products}
                placeholder={settings.placeholderImage}
                unavailableForOrderAhead={
                  !selectedCategory.availableForOrderAhead
                }
                coffeeWizard={coffeeWizard}
              />
            )}
          </ContentWrapper>
        )}
      </ProductsLayoutWithLeftNav>
    </>
  )
}

ProductCategoryPage.propTypes = {
  categories: PropTypes.arrayOf(category).isRequired,
  selectedCategory: category.isRequired,
  isTransactional: PropTypes.bool.isRequired,
  banner: PropTypes.object,
  ...commonPageProps
}

export default ProductCategoryPage
